// All visible site copy. Single source of truth.
// Shipping numbers are NOT written here: they come from lib/policies.ts.

import { PROMO_BAR, SHIPPING, flatShippingLabel, freeShippingOverLabel } from './policies';

export const home = {
  promoBar: PROMO_BAR,
  hero: {
    title:    'Scent, made personal.',
    subtitle: 'Curated fragrances for the everyday — luxurious without the luxury markup.',
    cta:      'Shop the collection',
  },
  features: [
    {
      number:   '01',
      headline: 'Curated, not catalog',
      body:     'Every fragrance in our shop has been hand-picked. No filler, no bulk lots.',
    },
    {
      number:   '02',
      headline: 'Priced for everyday',
      body:     'Quality scents at a price you can actually wear daily — not a once-a-year splurge.',
    },
    {
      number:   '03',
      headline: 'Personal recommendations',
      body:     "Tell us how you want to feel. We'll suggest a scent. Free, anytime.",
    },
  ],
  // Not "best sellers": there is no sales data to back that, so the rail is
  // plain "Featured" and only ever shows in-stock products.
  featured: {
    eyebrow: 'Featured',
    cta:     'View more',
  },
  storyTeaser: {
    title: 'Our story',
    body:
      "Noir Pétale started with a belief: that scent is the most memory-tied of all the senses, " +
      "and that everyone deserves a signature one — not just the people who can drop $300 on a bottle.",
    cta: 'Read more',
  },
  newsletter: {
    title:       'Get first access to new arrivals',
    body:        "Sign up and we'll let you know when something special comes in. No spam, unsubscribe anytime.",
    placeholder: 'Email address',
    button:      'Sign me up',
    successMsg:  "You're on the list. First access incoming.",
    errorMsg:    "Couldn't sign you up — try again or email us directly.",
  },
} as const;

// Nav structure supports optional `children` for hover dropdowns on desktop
// and accordion-style expand on mobile. Categories below are provisional —
// awaiting Tanisha's real catalog structure (see iMessage draft).
//
// Three item shapes are supported:
//   • plain link             → just navigates
//   • link with `children`   → small dropdown panel (text only)
//   • link with `mega`       → full-width Kilian-style drawer with images
export type NavLink = {
  label:    string;
  href:     string;
  badge?:   'new' | 'sale';
  featured?: boolean;
};

export type NavMegaSection = {
  title:    string;
  // Visual treatment for the panel:
  //   - src: full-bleed photo (preferred when available)
  //   - gradient: Tailwind gradient stops (e.g. 'from-rose/30 via-bone to-gold/15')
  //   - label: short serif-italic word centered on the gradient when there's
  //     no photo — gives empty panels an editorial feel rather than "missing
  //     image" energy.
  image:    { src?: string; alt?: string; gradient?: string; label?: string };
  links:    NavLink[];
};

export type NavItem = NavLink & {
  children?: NavLink[];
  mega?:     NavMegaSection[];
};

import { TOP_BRANDS } from './brands';

export const nav: {
  primary: NavItem[];
  secondary: NavLink[];
  legal: NavLink[];
  socials: NavLink[];
} = {
  primary: [
    {
      label: 'Shop All',
      href:  '/shop',
      mega: [
        {
          title: 'The Collection',
          image: {
            src:      '/hero/bestseller.jpg',
            alt:      'A perfume bottle nestled in apricot blossoms — editorial flatlay.',
            gradient: 'from-surface to-surface',
          },
          links: [
            { label: 'All Fragrances',      href: '/shop', featured: true },
            { label: 'Decants & Samples',   href: '/decants' },
            { label: 'Discovery Set',       href: '/discovery-set' },
            { label: 'Build a Set',         href: '/build-a-set' },
          ],
        },
        {
          title: 'By Size',
          image: {
            gradient: 'from-surface to-surface',
            label:    'Maison',
          },
          links: [
            { label: 'Discovery Size', href: '/shop?size=mini' },
            { label: 'Travel Size',    href: '/shop?size=travel' },
            { label: 'Full Bottles',   href: '/shop?size=full' },
          ],
        },
        {
          title: 'For Everyone',
          image: {
            gradient: 'from-surface to-surface',
            label:    'Découvrez',
          },
          links: [
            { label: 'For Her', href: '/for-her' },
            { label: 'For Him', href: '/for-him' },
            { label: 'Unisex',  href: '/unisex' },
          ],
        },
      ],
    },
    { label: 'For Her', href: '/for-her' },
    { label: 'For Him', href: '/for-him' },
    { label: 'Unisex',  href: '/unisex' },
    {
      label: 'Brands',
      href:  '/brands',
      mega: [
        {
          title: 'Most Loved',
          image: {
            gradient: 'from-surface to-surface',
            label:    'Aimée',
          },
          links: [
            { label: TOP_BRANDS[0].name, href: `/brands/${TOP_BRANDS[0].slug}`, featured: true }, // Lattafa
            { label: TOP_BRANDS[1].name, href: `/brands/${TOP_BRANDS[1].slug}` },                 // BSB
            { label: TOP_BRANDS[2].name, href: `/brands/${TOP_BRANDS[2].slug}` },                 // Sand+Fog
          ],
        },
        {
          title: 'Designer',
          image: {
            src:      '/hero/about.jpg',
            alt:      'A glass perfume bottle photographed against an iridescent abstract backdrop.',
            gradient: 'from-surface to-surface',
          },
          links: [
            { label: TOP_BRANDS[5].name,  href: `/brands/${TOP_BRANDS[5].slug}` },                // Dolce & Gabbana
            { label: TOP_BRANDS[9].name,  href: `/brands/${TOP_BRANDS[9].slug}` },                // YSL
            { label: TOP_BRANDS[10].name, href: `/brands/${TOP_BRANDS[10].slug}` },               // Valentino DBIR
            { label: TOP_BRANDS[11].name, href: `/brands/${TOP_BRANDS[11].slug}` },               // Lancome
          ],
        },
        {
          title: 'Niche',
          image: {
            gradient: 'from-surface to-surface',
            label:    'Niche',
          },
          links: [
            { label: TOP_BRANDS[7].name, href: `/brands/${TOP_BRANDS[7].slug}` },                 // Gulf Orchid
            { label: TOP_BRANDS[8].name, href: `/brands/${TOP_BRANDS[8].slug}` },                 // Ahuja
            { label: TOP_BRANDS[4].name, href: `/brands/${TOP_BRANDS[4].slug}` },                 // Jo Milano
            { label: TOP_BRANDS[6].name, href: `/brands/${TOP_BRANDS[6].slug}` },                 // Ulta
            { label: TOP_BRANDS[3].name, href: `/brands/${TOP_BRANDS[3].slug}` },                 // Victoria's Secret
          ],
        },
        {
          title: 'Explore All',
          image: {
            gradient: 'from-surface to-surface',
            label:    'Maison',
          },
          links: [
            { label: 'View All Brands', href: '/brands', featured: true },
            { label: 'Discovery Sets',  href: '/discovery-set' },
          ],
        },
      ],
    },
  ],
  secondary: [
    { label: 'Discovery', href: '/discovery-set' },
    { label: 'About',     href: '/about' },
    { label: 'Contact',   href: '/contact' },
  ],
  legal: [
    { label: 'Terms & Conditions',    href: '/terms' },
    { label: 'Privacy Policy',        href: '/privacy' },
    { label: 'Shipping & Returns',    href: '/shipping-returns' },
    { label: 'FAQ',                   href: '/faq' },
    { label: 'Accessibility',         href: '/accessibility' },
  ],
  socials: [
    { label: 'Instagram', href: 'https://instagram.com/noirpetale.co' },
  ],
};

export const about = {
  title: 'Our story',
  paragraphs: [
    "Noir Pétale was born from a simple idea — that scent is the most memory-tied of all our senses, and the people who wear it every day deserve fragrance that lives up to that.",
    "Most of the scents we love sit behind premium markups that don't reflect what's in the bottle. We started Noir Pétale to change that. Every fragrance in our shop has been hand-picked — quality scents from makers who care about the craft, priced for the people who'd wear them every day.",
    "We're not a department store. We're a curated shop run by people who genuinely love fragrance.",
  ],
  beliefs: [
    { headline: 'Scent is personal.', body: 'No two people wear the same fragrance the same way. We help you find yours.' },
    { headline: "Quality doesn't have to cost three figures.", body: "The best perfumers don't always live in Paris." },
    { headline: 'Recommendations beat algorithms.', body: 'Tell us how you want to feel and we\'ll point you to something that fits.' },
  ],
} as const;

export const faq = [
  { q: 'How are your fragrances priced compared to retail?',
    a: 'We curate and resell — our pricing reflects sourcing efficiencies, not lower-quality product. The bottles you receive are exactly as described, sealed, and authentic.' },
  { q: 'Are these authentic fragrances?',
    a: 'Yes. Every bottle in our shop comes from authorized channels.' },
  { q: 'How long does shipping take?',
    a: `${SHIPPING.label} is a flat ${flatShippingLabel} within the United States, free on orders over ${freeShippingOverLabel}, and takes ${SHIPPING.transitDays} business days.` },
  { q: 'Do you ship internationally?',
    a: "Not yet — currently US only. Sign up for our newsletter and we'll let you know when that changes." },
  { q: "What's your return policy?",
    a: 'Unopened, sealed bottles can be returned within 14 days of delivery for a full refund. Once opened, bottles cannot be returned for hygiene and authenticity reasons.' },
  { q: 'Can I get a recommendation?',
    a: "Yes — email us at noirpetale.co@gmail.com with a fragrance you've loved before or the mood you want, and we'll send you 2–3 suggestions for free." },
] as const;
